import React from 'react';

import closeIcon from '@assets/img/icons/close-light.png';
import { Button } from '@components/Button';
import { VerticalSpace } from '@components/VerticalSpace';
import { AuthManager } from '@store/AuthManager';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    agree: '同意する',
    cookieAgreement: () => (
      <>
        このサイトはCookie(クッキー)を利用しています。
        <br />
        このサイトは快適な閲覧のためにCookieを使用しています。Cookieの使用に同意いただける場合は、「同意する」をクリックしてください。詳細については
        <Link to={url('/about/CookiePolicy')}>Cookieポリシー</Link>
        をご確認ください。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  cookieAgreementContainer: {
    ...common.flexColumn,
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: theme.palette.common.white,
    minHeight: 140,
  },
  closeBtn: {
    position: 'absolute',
    right: '15px',
    top: '15px',
    width: '10px',
    height: '10px',
    cursor: 'pointer',
  },
  cookieAgreementContent: {
    ...common.flexCenter,
    maxWidth: 1400,
    margin: '0 auto',
    padding: '0 1rem',
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      ...common.itemsStretch,
      padding: '2rem 1rem',
    },
  },
  cookieAgreementText: {
    ...common.middleText,
    color: theme.palette.common.white,
    flex: 5,
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  cookieAgreementButton: {
    flex: 1,
    ...common.flexInline,
    justifyContent: 'flex-end',
  },
  applyForCookieAgreement: {
    padding: '12px 43px',
  },
}));

const _CookiePolicy: React.FC = observer(() => {
  const authManager = AuthManager.shared;
  const isMobile = useIsMobile();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderCookieDialog = () => (
    <div className={classes.cookieAgreementContainer}>
      <div
        className={classes.closeBtn}
        onClick={() => {
          authManager.setCookieDismissed(true);
          authManager.clear();
        }}
      >
        <img
          src={closeIcon}
          alt=""
          height={10}
          width={10}
          style={{
            cursor: 'pointer',
          }}
        />
      </div>
      <div className={classes.cookieAgreementContent}>
        <div className={classes.cookieAgreementText}>{strings.cookieAgreement()}</div>
        {isMobile && <VerticalSpace height={32} />}
        <div className={classes.cookieAgreementButton}>
          <Button
            className={classes.applyForCookieAgreement}
            color="primary"
            text={strings.agree}
            onClick={() => {
              authManager.setCookieAccepted('true');
              authManager.setCookieDismissed(true);
            }}
          />
        </div>
      </div>
    </div>
  );

  return !authManager.cookieDismissed ? renderCookieDialog() : null;
});

// Playwright doesn't handle higher order components
// https://github.com/microsoft/playwright/issues/19042
export const CookiePolicy: React.FC = () => <_CookiePolicy />;
